import {
  clearRecordingStartedAt,
  readRecordingStartedAt,
  setRecordingStartedAt,
} from '@/lib/recording/recordingStartedAt';
import { type Store } from '@/store';
import { formatDurationFromSeconds } from '@/utils/dates';
import { createModel } from '@rematch/core';
import { type RootModel } from '.';
// import { disableNoSleep, enableNoSleep } from '@/lib/noSleep';

/**
 * Reach the Rematch store at CALL time, never at module-evaluation time.
 *
 * This is load-bearing, not a style choice. `@/store` imports `./models`, and
 * `./models` imports THIS module, so the three are a cycle. Entering that cycle
 * at `@/store` is fine — nothing in this module's own scope needs the store, so
 * this file finishes evaluating and `./models` gets its `timerModel` slice. But
 * a module-scope `import { store }` here makes the cycle entrable from this
 * side too, and from this side it throws: `./models` re-enters while the
 * `timerModel` const below is still in its temporal dead zone, so its very
 * first read fails with `Cannot access 'timerModel' before initialization` and
 * takes down every module that transitively imported this one.
 *
 * That made `@/store` the only safe door into the cycle and every importer of
 * this file a latent import-time crash, depending only on which entry point
 * reached the cycle first. Resolving on demand deletes this module's outgoing
 * module-scope edge, so there is no longer a cycle to enter from any side.
 * The `Store` import above is `import type`, which is erased and creates no
 * edge; a value or side-effect import would reinstate one.
 */
function getStore(): Store {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- a static import re-forms the cycle documented above
  return (require('@/store') as { store: Store }).store;
}

export const convertSecondsToTime = (seconds: number, shorten = false): string =>
  formatDurationFromSeconds(seconds, { shorten });

class Timer {
  private static instance: Timer | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private startingSeconds: number = 0;
  private frozenStopRetryDuration: number | null = null;

  private constructor() {} // Constructor is private to ensure Singleton pattern

  public static getInstance(): Timer {
    if (!Timer.instance) {
      Timer.instance = new Timer();
    }
    return Timer.instance;
  }

  private beginTicking() {
    this.timer = setInterval(() => {
      const tickStartedAt = readRecordingStartedAt();
      if (tickStartedAt === null) {
        return;
      }

      const currentSessionSeconds = Math.floor((Date.now() - tickStartedAt) / 1000);
      getStore().dispatch.timerModel.setSeconds(
        currentSessionSeconds + this.startingSeconds
      );
    }, 1000);
  }

  start(startingSeconds: number = 0) {
    if (this.timer) return; // Prevent creating a new interval if timer is already running

    // enableNoSleep();
    this.frozenStopRetryDuration = null;
    this.startingSeconds = startingSeconds;
    // The register owns the instant; the slice copy is a mirror of it, kept for
    // the two readers of `state.timerModel.startTime`.
    const startedAt = Date.now();
    setRecordingStartedAt(startedAt);
    getStore().dispatch.timerModel.setRunning(true);
    getStore().dispatch.timerModel.setStartTime(startedAt);
    getStore().dispatch.timerModel.setSeconds(this.startingSeconds); // Initialize with startingSeconds

    this.beginTicking();
  }
  stop() {
    if (this.timer) {
      // disableNoSleep();
      clearInterval(this.timer);
      getStore().dispatch.timerModel.setRunning(false);
      getStore().dispatch.timerModel.setSeconds(0);
      this.timer = null;
      clearRecordingStartedAt(); // Reset the start time
    }
  }

  /**
   * Stop the timer and set the final duration to an authoritative value
   * instead of resetting to 0. Used by Chronicle stop flow where the
   * Rust upload pipeline provides the definitive session duration.
   */
  stopWithDuration(durationSeconds: number) {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.frozenStopRetryDuration = null;
    clearRecordingStartedAt();
    getStore().dispatch.timerModel.setRunning(false);
    getStore().dispatch.timerModel.setSeconds(durationSeconds);
  }

  pause() {
    if (this.timer) {
      // disableNoSleep();
      clearInterval(this.timer);
      getStore().dispatch.timerModel.setRunning(false);
      this.timer = null;
    }
  }

  /**
   * Freeze elapsed seconds while preserving the recording-in-progress signal.
   *
   * Offline stop can spend tens of seconds finalizing native audio. During that
   * window the clock must not reach the next minute, but lifecycle guards must
   * continue to treat the recording as active until finalization resolves.
   */
  freeze() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  resumeFrozen() {
    if (this.timer || readRecordingStartedAt() === null) return;
    this.beginTicking();
  }

  clearFrozenStopRetryDuration() {
    this.frozenStopRetryDuration = null;
  }

  pinFrozenStopRetryDuration(durationSeconds: number) {
    this.frozenStopRetryDuration = durationSeconds;
  }

  consumeFrozenStopRetryDuration(): number | null {
    const durationSeconds = this.frozenStopRetryDuration;
    this.frozenStopRetryDuration = null;
    return durationSeconds;
  }
}

export function clearFrozenStopRetryDuration(): void {
  Timer.getInstance().clearFrozenStopRetryDuration();
}

export function pinFrozenStopRetryDuration(durationSeconds: number): void {
  Timer.getInstance().pinFrozenStopRetryDuration(durationSeconds);
}

export function consumeFrozenStopRetryDuration(): number | null {
  return Timer.getInstance().consumeFrozenStopRetryDuration();
}

/**
 * Stop the shared recording timer and clear its state, whatever state it is in.
 *
 * `Timer.stop()` alone is NOT enough, and that is the whole reason this exists
 * rather than a `dispatch.timerModel.onStop()` at the call site: it is wrapped
 * in `if (this.timer)`, so once anything has cleared the interval handle —
 * `pause()` and `freeze()` do exactly that and deliberately leave `seconds`
 * alone — it is a no-op and `isRunning` keeps whatever value the store last
 * saw. The `clear()` is what actually guarantees the invariant.
 *
 * `isRunning` is not just a label on a UI clock. `runRecorderSafetyStop`
 * (`src/hooks/mutation/useMutateStopRecording.ts`) returns early on it twice —
 * once on entry and again after its `get_recording_state()` await — so a flag
 * left true disables the last-resort force-stop for every later runaway, and
 * `isRecordingInProgress` (`src/hooks/desktop/useChronicleSessionControl.ts`)
 * reports a recording in progress off it as layer 1.
 */
export function stopSharedRecordingTimer(): void {
  Timer.getInstance().stop();
  getStore().dispatch.timerModel.clear();
}

export function isSharedRecordingTimerRunning(): boolean {
  return getStore().getState().timerModel.isRunning;
}

export type InitialState = {
  seconds: number;
  startTime: number | null;
  isRunning: boolean;
};

const initialState: InitialState = {
  seconds: 0,
  startTime: null,
  isRunning: false,
};

export const timerModel = createModel<RootModel>()({
  state: initialState,
  selectors: (slice) => ({
    hasStartedTimer() {
      return slice((state) => {
        const { seconds } = state;
        return seconds > 0;
      });
    },
  }),
  reducers: {
    setSeconds(state: InitialState, seconds: number) {
      return { ...state, seconds };
    },
    setRunning(state: InitialState, isRunning: boolean) {
      return { ...state, isRunning };
    },
    setStartTime(state: InitialState, startTime: number) {
      return { ...state, startTime };
    },
    clear() {
      return initialState;
    },
  },
  effects: () => ({
    onStart(startingSeconds?: number) {
      Timer.getInstance().start(startingSeconds || 0);
    },
    onFreeze() {
      Timer.getInstance().freeze();
    },
    onPause() {
      Timer.getInstance().pause();
    },
    onResumeFrozen() {
      Timer.getInstance().resumeFrozen();
    },
    onReset() {
      Timer.getInstance().stop();
    },
    onStop() {
      Timer.getInstance().stop();
    },
    /**
     * Stop the timer and set the final duration to an authoritative value.
     * Used by Chronicle stop flow where the Rust upload pipeline returns
     * the definitive `durationMs` via `chronicle_session_stop`.
     */
    onStopWithDuration(durationSeconds: number) {
      Timer.getInstance().stopWithDuration(durationSeconds);
    },
  }),
});
